/**
 * CAG Cart Drawer Upsell
 * One-tap, clan-matched add-on row rendered below the checkout button.
 * Tier 1 (clan): fetches candidates from /search?view=clan-upsell and picks by
 * product-type priority, excluding families already in the cart.
 * Tier 2 (pinned): server-rendered row, this component only wires the button.
 * Suppressed for the rest of the session once an offer is accepted.
 */

const FAMILY = {
  'Mug': 'mug',
  'Accent Mug': 'mug',
  'Ceramic Ornament': 'ornament',
  'Christmas Ornament': 'ornament',
  'Framed Ornament': 'ornament',
  'Coaster': 'coaster',
  'Tumbler': 'tumbler',
  'Pennant': 'pennant',
};
const PRIORITY = ['mug', 'ornament', 'coaster', 'tumbler', 'pennant'];
const ACCEPTED_KEY = 'cag-upsell-added';

class CagCartUpsell extends HTMLElement {
  connectedCallback() {
    let accepted = null;
    try { accepted = sessionStorage.getItem(ACCEPTED_KEY); } catch (e) {}
    if (accepted === '1') { this.remove(); return; }
    if (!this.closest('.cart-drawer')) { this.remove(); return; }

    if (this.dataset.mode === 'pinned') {
      this.#wire(this.dataset.vid);
      this.hidden = false;
      return;
    }
    this.#load();
  }

  async #load() {
    const endpoint = this.dataset.endpoint;
    if (!endpoint) { this.remove(); return; }
    try {
      let items;
      const cacheKey = 'cag-upsell-cache:' + endpoint;
      let cached = null;
      try { cached = sessionStorage.getItem(cacheKey); } catch (e) {}
      if (cached) {
        items = JSON.parse(cached);
      } else {
        const res = await fetch(endpoint, { headers: { Accept: 'application/json' } });
        if (!res.ok) throw new Error('cag-upsell: fetch failed');
        const text = await res.text();
        items = JSON.parse(text);
        try { sessionStorage.setItem(cacheKey, JSON.stringify(items)); } catch (e) {}
      }
      const pick = this.#pick(items);
      if (!pick) { this.remove(); return; }
      this.#render(pick);
      this.#wire(String(pick.vid));
      this.hidden = false;
    } catch (e) {
      this.remove();
    }
  }

  #pick(items) {
    if (!Array.isArray(items)) return null;
    const inCart = new Set(
      (this.dataset.types || '')
        .split('||')
        .map((t) => FAMILY[t.trim()])
        .filter(Boolean)
    );
    for (const fam of PRIORITY) {
      if (inCart.has(fam)) continue;
      const candidate = items.find((i) => i && i.vid && FAMILY[i.type] === fam);
      if (candidate) return candidate;
    }
    return null;
  }

  #render(item) {
    let clan = this.dataset.clan || '';
    if (clan && clan === clan.toLowerCase()) {
      clan = clan.charAt(0).toUpperCase() + clan.slice(1);
    }

    const row = document.createElement('div');
    row.className = 'cag-upsell__row';

    if (item.img) {
      const img = document.createElement('img');
      img.className = 'cag-upsell__img';
      img.src = item.img;
      img.alt = item.title || '';
      img.width = 44;
      img.height = 44;
      img.loading = 'lazy';
      row.appendChild(img);
    }

    const text = document.createElement('div');
    text.className = 'cag-upsell__text';

    const eyebrow = document.createElement('span');
    eyebrow.className = 'cag-upsell__eyebrow';
    eyebrow.textContent = clan ? 'Complete your ' + clan + ' collection' : 'You might also like';
    text.appendChild(eyebrow);

    const title = document.createElement('a');
    title.className = 'cag-upsell__title';
    title.href = item.url || '#';
    title.textContent = item.title || '';
    text.appendChild(title);

    const meta = document.createElement('span');
    meta.className = 'cag-upsell__meta';
    let label = item.vtitle || '';
    if (label.indexOf(' - ') !== -1) label = label.split(' - ').slice(1).join(' - ');
    if (label === 'Default Title') label = '';
    meta.textContent = label ? label + ' \u00b7 ' + (item.price || '') : item.price || '';
    text.appendChild(meta);

    row.appendChild(text);

    const btn = document.createElement('button');
    btn.type = 'button';
    btn.className = 'cag-upsell__add';
    btn.textContent = 'Add';
    row.appendChild(btn);

    this.replaceChildren(row);
  }

  #wire(variantId) {
    const btn = this.querySelector('.cag-upsell__add');
    if (!btn || !variantId) { this.remove(); return; }
    btn.addEventListener('click', () => this.#add(btn, variantId));
  }

  async #add(btn, variantId) {
    btn.disabled = true;
    try {
      const formData = new FormData();
      formData.append('id', variantId);
      formData.append('quantity', '1');

      const sectionIds = [];
      document.querySelectorAll('cart-items-component').forEach((el) => {
        if (el instanceof HTMLElement && el.dataset.sectionId) sectionIds.push(el.dataset.sectionId);
      });
      if (sectionIds.length) formData.append('sections', sectionIds.join(','));

      const addUrl =
        (window.Theme && window.Theme.routes && window.Theme.routes.cart_add_url) ||
        this.dataset.addUrl ||
        '/cart/add';

      const res = await fetch(addUrl, {
        method: 'POST',
        headers: { Accept: 'application/json' },
        body: formData,
      });
      const response = await res.json();

      if (response.status) {
        btn.disabled = false;
        return;
      }

      try { sessionStorage.setItem(ACCEPTED_KEY, '1'); } catch (e) {}

      let cart;
      try {
        cart = await fetch('/cart.js').then((r) => r.json());
      } catch (e) {
        cart = undefined;
      }

      this.dispatchEvent(
        new CustomEvent('cart:update', {
          bubbles: true,
          detail: {
            resource: cart,
            sourceId: 'cag-cart-upsell',
            data: {
              source: 'cag-cart-upsell',
              itemCount: 1,
              variantId: String(variantId),
              sections: response.sections,
            },
          },
        })
      );
      this.remove();
    } catch (e) {
      btn.disabled = false;
    }
  }
}

if (!customElements.get('cag-cart-upsell')) {
  customElements.define('cag-cart-upsell', CagCartUpsell);
}
